import { useDispatch, useSelector } from 'react-redux';
import { invokeIfExists, rm, NO_ELEMENT } from 'utils';
import { getChannel, getTopSellers } from 'views/ProductPage/selector';
import { isMobile } from 'components/Shared/responsive';
import { IconArrow } from 'components/Shared/Icon/themes/BGH-RD3/iconArrow';
import { ProductCard } from 'components/Shared/Product';
import { Button } from 'components/Button';
import { CompareProduct, TopSellerHighlight } from 'models/CompareProduct';
import styles from './topSellers.scss';
import cn from 'classnames';
import { ProductSection } from 'views/ProductPage/themes/BGH/sections/productSection';
import { Tag } from 'components/Tag';
import { ProductlistViewProduct } from 'models/ProductlistViewProduct';
import { SrcSet } from 'components/Shared/Image/SrcSet';
import { PageState } from 'models/PageState';
import { getTopSellerListName, mapListViewProductToGtm } from 'store/googleTagManager/events/utils';
import { GtmSelectItem, GtmViewListItem } from 'store/googleTagManager/actions';
import { useIntersectionObserver } from 'hooks';
import { TrackedElement } from 'hooks/useIntersectionObserver';
import { ListViewProduct } from 'models/ListViewProduct';

const MOBILE_WIDTH = 148;
const DESKTOP_WIDTH = 232;
const IMAGE_MARGIN = 2;

const picture = {
  height: DESKTOP_WIDTH + IMAGE_MARGIN,
  width: DESKTOP_WIDTH + IMAGE_MARGIN
};

const sizes: { [key: string]: SrcSet } = {
  default: {
    viewWidth: MOBILE_WIDTH,
    width: MOBILE_WIDTH,
    height: MOBILE_WIDTH
  },
  medium: {
    viewWidth: DESKTOP_WIDTH,
    width: DESKTOP_WIDTH,
    height: DESKTOP_WIDTH
  }
};

const highlightText = (k: TopSellerHighlight) =>
  invokeIfExists(
    {
      [TopSellerHighlight.Bestseller]: () => rm('product_bestseller_title'),
      [TopSellerHighlight.TopTested]: () => rm('product_toptested_title'),
      [TopSellerHighlight.BestDiscount]: () => rm('product_highest_discount_title')
    },
    k
  );

const borderStyle = (p: Pick<CompareProduct, 'highlight'>) => ({
  [styles.border]: p.highlight !== undefined,
  [styles.red]: p.highlight === TopSellerHighlight.BestDiscount,
  [styles.black]: p.highlight === TopSellerHighlight.TopTested
});

export const TopSellers = () => {
  const channel = useSelector<PageState, string>(getChannel);
  const mobile = useSelector(isMobile);
  const topSellers = useSelector(getTopSellers);
  const reduxDispatch = useDispatch();

  const { setElementRef } = useIntersectionObserver<CompareProduct>({
    onBatchVisible: handleElementsVisible
  });

  if (!topSellers) {
    return NO_ELEMENT;
  }

  const topSellersToShow =
    channel !== 'BNO'
      ? topSellers
      : {
          products: topSellers.products.slice(0, 4),
          attributes: topSellers.attributes
        };

  const gtmOnProductClick = (product: ListViewProduct, index: number) => {
    const listName = getTopSellerListName(product);
    const gtmItemProduct = mapListViewProductToGtm({
      product,
      indexInList: index,
      listName
    });

    reduxDispatch(GtmSelectItem({ gtmItemProduct }));
  };

  function handleElementsVisible(elements: TrackedElement<CompareProduct>[]) {
    const items = elements.map(element => {
      const viewedProduct = element.payload;
      const listViewProduct = viewedProduct as unknown as ListViewProduct;

      const productIndex = topSellersToShow.products.indexOf(viewedProduct);
      const listName = getTopSellerListName(listViewProduct);

      return mapListViewProductToGtm({
        product: listViewProduct,
        indexInList: productIndex >= 0 ? productIndex : 0,
        listName
      });
    });

    reduxDispatch(GtmViewListItem({ gtmItemProducts: items }));
  }

  return (
    <ProductSection title={rm('product_topsellers_header')}>
      <div className={styles.section}>
        <table className={styles.container}>
          <tbody>
            <tr>
              <td className={styles.hide} />
              {topSellersToShow.products.map(p => (
                <td key={p.productId}>
                  {p.highlight !== undefined && (
                    <div className={styles.cta}>
                      <Tag
                        text={highlightText(p.highlight)}
                        className={cn(styles.highlight, borderStyle(p))}
                      />
                    </div>
                  )}
                </td>
              ))}
            </tr>
            <tr>
              <td className={styles.hide}>{rm('product_topsellers_info_time')}</td>
              {topSellersToShow.products.map((product, index) => (
                <td key={product.productId}>
                  <ProductCard
                    ref={node => setElementRef({ ref: node, payload: product })}
                    isDisabled={index === 0}
                    page="topsellers"
                    customSizes={sizes}
                    picture={{
                      ...picture,
                      className: cn(borderStyle(product))
                    }}
                    onLinkClick={() =>
                      gtmOnProductClick(product as unknown as ListViewProduct, index)
                    }
                    {...(product as unknown as ProductlistViewProduct)}
                  />
                </td>
              ))}
            </tr>
            {Object.entries(topSellersToShow.attributes).map(([key, value]) => (
              <tr key={key} className={styles.attribute}>
                {!mobile ? (
                  <>
                    <th>{value}</th>
                    {topSellersToShow.products.map(p => (
                      <td key={p.productId}>{p.attributes[key]}</td>
                    ))}
                  </>
                ) : (
                  <>
                    {topSellersToShow.products.map((p, i) => (
                      <>
                        <td key={p.productId} className={cn({ [styles.mobile]: i === 0 })}>
                          {i === 0 && <div>{value}</div>}
                          {p.attributes[key]}
                        </td>
                      </>
                    ))}
                  </>
                )}
              </tr>
            ))}
            <tr className={styles.buttons}>
              <td className={styles.hide} />
              <td />
              {topSellersToShow.products.slice(1).map((product, index) => (
                <td key={product.productId}>
                  <Button
                    appearance="tertiary"
                    className={styles.button}
                    onClick={() =>
                      gtmOnProductClick(product as unknown as ProductlistViewProduct, index)
                    }
                    href={product.url}
                  >
                    {rm('product_goto')} <IconArrow size="tiny" />
                  </Button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </ProductSection>
  );
};
